import { DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import Dialog from "components/NoScrollDialog";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useEffect, useState } from "react";
import auditsService from "services/audits-service";
import useLoading from "utils/useLoading";
import Avatar from "components/FileAvatar";

export default function AuditReceiptDialog({ audit, refresh }) {
  if (!audit.auditReceipt) return;
  const [updated, setUpdated] = useState({});
  const { loading, changeLoading } = useLoading({
    submit: false,
  });
  const [open, setOpen] = useState(false);

  async function onSubmit() {
    changeLoading("submit", true);
    const data = { ...updated };

    if (
      audit.auditReceipt?.attachment != data.attachment &&
      Object.keys(data).includes("attachment")
    ) {
      if (!data.attachment) {
        const [response] = await auditsService.deleteAuditReceiptAttachment(
          audit.id,
        );
        if (!response.ok) {
          return { error: true };
        }
      } else {
        const [response] = await auditsService.updateAuditReceiptAttachment(
          audit.id,
          [data.attachment],
        );
        if (!response.ok) {
          return { error: true };
        }
      }
    }

    delete data.attachment;

    if (Object.keys(data).length > 0) {
      const [response] = await auditsService.upsertAuditReceipt(audit.id, data);

      if (!response.ok) {
        return { error: true };
      }
    }

    await refresh();

    setUpdated({});
    changeLoading("submit", false);

    return { success: true };
  }

  useEffect(() => {
    setUpdated({ ...updated, attachment: audit.auditReceipt.attachment });
  }, [audit]);

  if (audit.auditTypeId != 2) return;

  return (
    <>
      <MDButton
        variant="gradient"
        color="info"
        onClick={() => {
          setOpen(true);
        }}
        size="small"
      >
        Receipt
      </MDButton>
      <Dialog
        open={open}
        maxWidth={"lg"}
        onClose={() => {
          setOpen(false);
        }}
        fullWidth
        PaperProps={{
          component: "form",
          onSubmit: async (event) => {
            event.preventDefault();

            changeLoading("submit", true);

            const result = await onSubmit(updated);
            if (result.success) {
              setOpen(false);
            }

            changeLoading("submit", false);
          },
        }}
      >
        <DialogTitle>Edit Receipt</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mt={0.5}>
            <Grid item xs={12}>
              <Avatar
                attachment={updated.attachment}
                setAttachment={(file) => {
                  setUpdated({ ...updated, attachment: file });
                }}
                sx={{
                  height: "320px",
                  margin: "0 auto",
                  width: "100%",
                  "& img": {
                    height: "100%",
                    width: "100%",
                    maxWidth: "500px",
                    objectFit: "contain",
                    objectPosition: "center",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Number"
                fullWidth
                value={updated.number ?? audit.auditReceipt?.number ?? ""}
                onChange={(e) => {
                  setUpdated({ ...updated, number: e.target.value });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Total"
                fullWidth
                type="number"
                value={updated.total ?? audit.auditReceipt?.total ?? ""}
                onChange={(e) => {
                  setUpdated({ ...updated, total: Number(e.target.value) });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Tips"
                fullWidth
                type="number"
                value={updated.tips ?? audit.auditReceipt?.tips ?? ""}
                onChange={(e) => {
                  setUpdated({ ...updated, tips: Number(e.target.value) });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Total Paid"
                fullWidth
                type="number"
                value={updated.totalPaid ?? audit.auditReceipt?.totalPaid ?? ""}
                onChange={(e) => {
                  setUpdated({ ...updated, totalPaid: Number(e.target.value) });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Shopper Age"
                fullWidth
                type="number"
                value={
                  updated.shopperAge ?? audit.auditReceipt?.shopperAge ?? ""
                }
                onChange={(e) => {
                  setUpdated({
                    ...updated,
                    shopperAge: Number(e.target.value),
                  });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DropdownSelect
                label={"Shopper Gender"}
                id="gender-selector"
                initialValue={
                  updated.shopperGender ??
                  audit.auditReceipt?.shopperGender ??
                  ""
                }
                items={[
                  { id: 1, label: "Male" },
                  { id: 2, label: "Female" },
                ]}
                onChange={(newValue) => {
                  setUpdated({
                    ...updated,
                    shopperGender: newValue.id,
                  });
                }}
                required={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Number of People"
                fullWidth
                type="number"
                value={
                  updated.numberOfPeople ??
                  audit.auditReceipt?.numberOfPeople ??
                  ""
                }
                onChange={(e) => {
                  setUpdated({
                    ...updated,
                    numberOfPeople: Number(e.target.value),
                  });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                label="Manager's Name"
                fullWidth
                value={
                  updated.managerName ?? audit.auditReceipt?.managerName ?? ""
                }
                onChange={(e) => {
                  setUpdated({
                    ...updated,
                    managerName: e.target.value,
                  });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                required
                label="Waiter's Name"
                fullWidth
                value={
                  updated.waiterName ?? audit.auditReceipt?.waiterName ?? ""
                }
                onChange={(e) => {
                  setUpdated({
                    ...updated,
                    waiterName: e.target.value,
                  });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                required
                multiline
                minRows={4}
                label="Ordered Items (Separated by new line)"
                fullWidth
                value={
                  updated.orderedItems ?? audit.auditReceipt?.orderedItems ?? ""
                }
                onChange={(e) => {
                  setUpdated({
                    ...updated,
                    orderedItems: e.target.value,
                  });
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            justifyContent: "end",
          }}
        >
          <MDButton
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </MDButton>
          <LoadingButton
            loading={loading.submit}
            type="submit"
            variant="gradient"
            color="info"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
